import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Modal from 'common/components/modal/Modal';
import styles from './DriverHistoryModal.module.scss';
import TrackingService from 'api/tracking/TrackingService';
import TripDriverHistoryItem from 'api/tracking/models/TripDriverHistoryItem';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import moment from 'moment';
import { FaTimes, FaArrowRight, FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const SOURCE_LABELS: Record<string, string> = {
    'TAG': 'location.driver_history.source_tag',
    'VERIFICATION': 'location.driver_history.source_verification',
    'RESERVATION': 'location.driver_history.source_reservation',
    'VEHICLE': 'location.driver_history.source_vehicle',
    'NONE': 'location.driver_history.source_none',
};

const getSourceEntityUrl = (action?: string, sourceEntityId?: string): string | null => {
    if (!sourceEntityId || sourceEntityId === EMPTY_GUID) return null;
    if (action?.startsWith('VERIFICATION'))
        return `/verifications/details/${sourceEntityId}`;
    if (action?.startsWith('RESERVATION'))
        return `/reservations/details/${sourceEntityId}`;
    return null;
};

const getSourceEntityUrlBySource = (source?: string, sourceEntityId?: string): string | null => {
    if (!sourceEntityId || sourceEntityId === EMPTY_GUID) return null;
    if (source === 'VERIFICATION')
        return `/verifications/details/${sourceEntityId}`;
    if (source === 'RESERVATION')
        return `/reservations/details/${sourceEntityId}`;
    if (source === 'VEHICLE')
        return `/vehicles/details/${sourceEntityId}/informations/list`;
    return null;
};

type SourceDates = { start?: string; end?: string };
type SideDates = { prev: SourceDates | null; new: SourceDates | null };
type SideEntityIds = { prev?: string; new?: string };

const formatTripDate = (date?: Date): string | undefined => {
    if (!date) return undefined;
    return moment(date).format('DD/MM/YYYY | HH:mm');
};

const parseSideEntityIds = (item: TripDriverHistoryItem): SideEntityIds => {
    if (!item.changes) return {};
    try {
        const changes = JSON.parse(item.changes);
        return {
            prev: changes.PrevSourceEntityId || undefined,
            new: changes.NewSourceEntityId || undefined,
        };
    } catch { return {}; }
};

const parseSideDates = (item: TripDriverHistoryItem): SideDates => {
    const tripDates: SourceDates = {
        start: formatTripDate(item.tripStartDate),
        end: formatTripDate(item.tripEndDate),
    };

    let prevFromJson: SourceDates | null = null;
    let newFromJson: SourceDates | null = null;

    if (item.changes) {
        try {
            const changes = JSON.parse(item.changes);
            const prevStart = changes.PrevSourceStartDate;
            const prevEnd = changes.PrevSourceEndDate;
            const newStart = changes.NewSourceStartDate;
            const newEnd = changes.NewSourceEndDate;
            const legacyStart = changes.SourceStartDate;
            const legacyEnd = changes.SourceEndDate;

            prevFromJson = (prevStart || prevEnd) ? { start: prevStart, end: prevEnd }
                : (legacyStart || legacyEnd) ? { start: legacyStart, end: legacyEnd } : null;
            newFromJson = (newStart || newEnd) ? { start: newStart, end: newEnd }
                : (legacyStart || legacyEnd) ? { start: legacyStart, end: legacyEnd } : null;
        } catch { /* ignore */ }
    }

    const prevSource = item.previousDriverSource;
    const newSource = item.newDriverSource;

    return {
        prev: (prevSource === 'TAG' || prevSource === 'VEHICLE') ? tripDates : prevFromJson,
        new: (newSource === 'TAG' || newSource === 'VEHICLE') ? tripDates : newFromJson,
    };
};

const hasSourceType = (source?: string) => source === 'VERIFICATION' || source === 'RESERVATION' || source === 'TAG' || source === 'VEHICLE';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    tripId: string;
    driverName?: string;
    resolvedDriverSource?: string;
    resolvedSourceEntityId?: string;
};

const DriverHistoryModal: React.FC<Props> = ({ isOpen, onClose, tripId, driverName, resolvedDriverSource, resolvedSourceEntityId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [historyItems, setHistoryItems] = useState<TripDriverHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (isOpen && tripId) {
            setLoading(true);
            setExpandedCards({});
            TrackingService.getTripDriverHistory(tripId)
                .then(res => {
                    setHistoryItems(res || []);
                })
                .catch(() => {
                    setHistoryItems([]);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isOpen, tripId]);

    const toggleExpand = (key: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setExpandedCards(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getActionLabel = (action: string) => {
        const labels: Record<string, string> = {
            'TRIP_CREATE': t('location.driver_history.action_trip_create') as string,
            'SYSTEM': t('location.driver_history.system') as string,
            'VERIFICATION_CREATE': t('location.driver_history.action_verification_create') as string,
            'VERIFICATION_EDIT': t('location.driver_history.action_verification_edit') as string,
            'VERIFICATION_DELETE': t('location.driver_history.action_verification_delete') as string,
            'RESERVATION_CREATE': t('location.driver_history.action_reservation_create') as string,
            'RESERVATION_EDIT': t('location.driver_history.action_reservation_edit') as string,
            'RESERVATION_DELETE': t('location.driver_history.action_reservation_delete') as string,
        };
        return labels[action] || action;
    };

    const getSourceLabel = (source?: string) => {
        if (!source) return '';
        const key = SOURCE_LABELS[source];
        return key ? t(key as any) as string : source;
    };

    const renderSourceBadge = (source?: string, action?: string, sourceEntityId?: string) => {
        if (!source || source === 'NONE' || source === 'ANONYMIZED') return <></>;
        const label = getSourceLabel(source);
        const url = getSourceEntityUrlBySource(source, sourceEntityId);
        const sourceClass = source === 'RESERVATION' ? styles.sourceBadgeReservation
            : source === 'VEHICLE' ? styles.sourceBadgeVehicle
            : source === 'TAG' ? styles.sourceBadgeTag : '';
        const badgeClass = `${styles.sourceBadge} ${sourceClass}`.trim();

        if (url) {
            return (
                <span
                    className={`${badgeClass} ${styles.sourceClickable}`}
                    onClick={e => { e.stopPropagation(); window.open(url, '_blank'); }}
                >
                    {label}
                </span>
            );
        }

        return <span className={badgeClass}>{label}</span>;
    };

    const getInitials = (name?: string) => {
        if (!name) return '';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const renderDriverCard = (
        itemId: string,
        side: 'prev' | 'new',
        label: string,
        name?: string,
        source?: string,
        action?: string,
        sourceEntityId?: string,
        sourceDates?: { start?: string; end?: string } | null
    ) => {
        const isAnonymized = source === 'ANONYMIZED';
        const isNoDriver = (!name && !isAnonymized) || source === 'NONE';
        const showInfo = hasSourceType(source) && sourceDates;
        const expandKey = `${itemId}_${side}`;
        const isExpanded = expandedCards[expandKey];

        const isReplaced = side === 'prev' && !isNoDriver;

        return (
            <div className={`${styles.driverCard} ${isNoDriver ? styles.driverCardEmpty : ''} ${isReplaced ? styles.driverCardReplaced : ''}`}>
                <div className={styles.driverCardLabel}>{label}</div>
                <div className={styles.driverCardContent}>
                    <div className={styles.driverCardInfo}>
                        <div className={styles.driverCardName}>
                            {isAnonymized ? t('privacy.anonymized_label') as string : isNoDriver ? t('location.driver_history.no_driver') as string : name}
                        </div>
                        {renderSourceBadge(source, action, sourceEntityId)}
                    </div>
                    {showInfo && (
                        <div className={styles.infoToggle} onClick={e => toggleExpand(expandKey, e)}>
                            {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                            <span>{t('location.driver_history.more_info') as string}</span>
                        </div>
                    )}
                </div>
                {showInfo && isExpanded && sourceDates && (() => {
                    const datesTooltipId = `dates_${expandKey}`;
                    const datesTooltip = (source === 'VERIFICATION')
                        ? t('location.driver_history.dates_verification') as string
                        : (source === 'RESERVATION')
                            ? t('location.driver_history.dates_reservation') as string
                            : t('location.driver_history.dates_trip') as string;
                    return (
                        <>
                            <div className={styles.infoDates} data-tip={datesTooltip} data-for={datesTooltipId}>
                                {sourceDates.start && (
                                    <div>{t('location.driver_history.field_start_date') as string}: {sourceDates.start}</div>
                                )}
                                {sourceDates.end && (
                                    <div>{t('location.driver_history.field_end_date') as string}: {sourceDates.end}</div>
                                )}
                            </div>
                            <ReactTooltip id={datesTooltipId} />
                        </>
                    );
                })()}
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} style={{ overlay: { zIndex: 10000 }, content: { maxHeight: '80vh', overflow: 'auto' } }}>
            <div className={styles.header}>
                <h3>{t('location.driver_history.title') as string}</h3>
                <button className={styles.closeBtn} onClick={onClose}><FaTimes /></button>
            </div>
            {resolvedDriverSource === 'ANONYMIZED' ? (
                <div className={styles.currentDriver}>
                    <span className={styles.currentDriverLabel}>{t('location.driver_history.current_driver') as string}: </span>
                    <span className={styles.currentDriverName}>{t('privacy.anonymized_label') as string}</span>
                </div>
            ) : driverName && resolvedDriverSource ? (
                <div className={styles.currentDriver}>
                    <span className={styles.currentDriverLabel}>{t('location.driver_history.current_driver') as string}: </span>
                    <span className={styles.currentDriverName}>{driverName}</span>
                    <span data-tip={`${t('location.driver_history.source_tooltip') as string} ${getSourceLabel(resolvedDriverSource)}`} data-for="currentDriverTooltip">
                        {renderSourceBadge(resolvedDriverSource, resolvedDriverSource, resolvedSourceEntityId)}
                    </span>
                    <ReactTooltip id="currentDriverTooltip" />
                </div>
            ) : <></>}
            {loading ? (
                <div className={styles.loading}><LoadingSpinner /></div>
            ) : historyItems.length === 0 ? (
                <div className={styles.empty}>{t('location.driver_history.no_history') as string}</div>
            ) : (
                <div className={styles.historyList}>
                    {historyItems.map(item => {
                        const userName = item.userCreatedId === EMPTY_GUID
                            ? t('location.driver_history.system') as string
                            : item.userName || '-';
                        const dateStr = moment(item.createdDate).format('DD/MM/YYYY | HH:mm');
                        const sideDates = parseSideDates(item);
                        const sideEntityIds = parseSideEntityIds(item);

                        return (
                            <div key={item.id} className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <span className={styles.cardAction}>
                                        {getActionLabel(item.action)}
                                        {item.action !== 'SYSTEM' && item.userCreatedId !== EMPTY_GUID && (
                                            <> {t('location.driver_history.by') as string} <b>{userName}</b></>
                                        )}
                                        {' '}{t('location.driver_history.on') as string} <b>{dateStr}</b>
                                    </span>
                                </div>
                                <div className={styles.cardBody}>
                                    {renderDriverCard(
                                        item.id, 'prev',
                                        t('location.driver_history.replaced') as string,
                                        item.previousDriverName,
                                        item.previousDriverSource,
                                        item.action,
                                        sideEntityIds.prev || item.sourceEntityId,
                                        sideDates.prev
                                    )}
                                    <div className={styles.cardArrow}>
                                        <FaArrowRight />
                                    </div>
                                    {renderDriverCard(
                                        item.id, 'new',
                                        t('location.driver_history.assigned') as string,
                                        item.newDriverName,
                                        item.newDriverSource,
                                        item.action,
                                        sideEntityIds.new || item.sourceEntityId,
                                        sideDates.new
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Modal>
    );
};

export default DriverHistoryModal;
